import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import { ProgressSpinner } from 'primereact/progressspinner'
import { classNames } from 'primereact/utils'
import { DragEvent, FC, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useRecoilState } from 'recoil'
import { apiClient } from '../../api/configs/axios-setup'
import { GifJob, listGifs } from '../../api/gif-API'
import { useGetHeadlineData, useGetHeadlineDataProjectRun } from '../../api/report-API'
import {
  getMavMediaForCases,
  getRunGeometryImages,
  MavMediaForCase,
  ReportRunCase,
  ReportRunSelection,
  uploadSectionMedia
} from '../../api/report-generator-API'
import {
  addRunMedia,
  getRunMedia,
  removeRunMediaItem,
  RunMediaInput,
  RunMediaItem,
  RunMediaRecord
} from '../../api/run-media-API'
import {
  applyMetricFilterParams,
  PostProcImage,
  PostProcImageFilters,
  PostProcImagesResponse
} from '../../api/submission-API'
import { GMMSRaceTeamKey, UploadTypes } from '../../utils/constants'
import { downloadFileDirectFromBlob } from '../../services/blobUploadService'
import { runMediaRunState } from '../../utils/state-atoms'
import CreateGifModal, { GifCaseOption } from './CreateGifModal'
import MarkupModal, { MarkupImage } from './MarkupModal'
import MetricMultiSelect from './MetricMultiSelect'

const MAX_IMAGES = 200

/** Post-proc image filters mirroring the Headline Data gallery. */
type PickerFilters = {
  imageType?: string | null
  section?: string | null
  location?: string | null
  /** Plain metrics, or "{metric}||{referenceRunId}" entries for compare deltas. */
  metricTypes?: string[] | null
}

/** A case (state) of the loaded run. */
type RunCase = { gmId: string; systemRunId: string; stateName?: string }

/** A browsable media candidate shown in the Add Media modal. */
type Candidate = {
  url: string
  caption: string
  type: 'image' | 'video'
  source: RunMediaInput['source']
  caseKey?: string
  caseName?: string
}

const parseHeadlineRows = (raw: any): Record<string, any>[] => {
  const cols = raw?.manifest?.schema?.columns
  const arr = raw?.result?.data_array
  if (!Array.isArray(cols) || !Array.isArray(arr)) return []
  return arr.map((row: any[]) => {
    const o: Record<string, any> = {}
    cols.forEach((c: any) => {
      o[c.name] = row[c.position]
    })
    return o
  })
}

const toNum = (v: unknown): number | undefined => {
  const n = typeof v === 'string' ? parseFloat(v) : (v as number)
  return typeof n === 'number' && !Number.isNaN(n) ? n : undefined
}

const fetchCaseImages = async (
  systemRunId: string,
  filters: PickerFilters = {},
  limit = MAX_IMAGES
): Promise<PostProcImagesResponse> => {
  const params = new URLSearchParams({ skip: '0', limit: String(limit) })
  if (filters.imageType) params.set('imageType', filters.imageType)
  if (filters.section) params.set('section', filters.section)
  if (filters.location) params.set('location', filters.location)
  applyMetricFilterParams(params, filters.metricTypes)
  const res = await apiClient.get<PostProcImagesResponse>(
    `/WorkflowSubmissions/cases/${encodeURIComponent(systemRunId)}/images?${params.toString()}`
  )
  return res.data
}

// Draft items not yet persisted to the server carry a temp id with this prefix.
const PENDING_PREFIX = 'pending-'
const isPending = (id: string) => id.startsWith(PENDING_PREFIX)
const newPendingId = () => `${PENDING_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2)}`

// Cache keys are scoped by team so switching teams in the same browser never
// restores or saves another team's pending media.
const teamScope = (): string => {
  try {
    return localStorage.getItem(GMMSRaceTeamKey) || 'noteam'
  } catch {
    return 'noteam'
  }
}

// Local cache of the unsaved working set so it survives navigating away.
// Scoped by team + project + run so same-named runs across projects don't collide.
const draftCacheKey = (runName: string, project?: string) =>
  `runMediaDraft:${teamScope()}:${project ?? ''}:${runName}`
const readDraftCache = (
  runName: string,
  project?: string
): { project?: string; items: RunMediaItem[]; markupLibrary?: RunMediaItem[] } | null => {
  try {
    const raw = localStorage.getItem(draftCacheKey(runName, project))
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}
const writeDraftCache = (
  runName: string,
  project: string | undefined,
  items: RunMediaItem[],
  markupLibrary: RunMediaItem[]
) => {
  try {
    localStorage.setItem(
      draftCacheKey(runName, project),
      JSON.stringify({ project, items, markupLibrary })
    )
  } catch {
    /* storage full / unavailable — non-fatal */
  }
}
const clearDraftCache = (runName: string, project?: string) => {
  try {
    localStorage.removeItem(draftCacheKey(runName, project))
  } catch {
    /* non-fatal */
  }
}

// Snapshot of the whole working session so returning to the tab restores the
// loaded run, its cases and the working media set without reloading the run.
const sessionCacheKey = () => `runMediaSession:${teamScope()}`
type RunMediaSession = {
  project?: string
  runName: string
  cases: RunCase[]
  runCases: ReportRunCase[]
  draftItems: RunMediaItem[]
  markupLibrary?: RunMediaItem[]
}
const readSessionCache = (): RunMediaSession | null => {
  try {
    const raw = localStorage.getItem(sessionCacheKey())
    return raw ? (JSON.parse(raw) as RunMediaSession) : null
  } catch {
    return null
  }
}
const writeSessionCache = (session: RunMediaSession) => {
  try {
    localStorage.setItem(sessionCacheKey(), JSON.stringify(session))
  } catch {
    /* non-fatal */
  }
}

const candidateToDraftItem = (c: Candidate): RunMediaItem => ({
  id: newPendingId(),
  blobPath: '',
  url: c.url,
  caption: c.caption,
  type: c.type,
  source: c.source ?? 'upload',
  caseKey: c.caseKey,
  caseName: c.caseName,
  timeAdded: Date.now()
})

interface RunMediaProps {
  onBuildReport?: (run: ReportRunSelection) => void
}

const RunMedia: FC<RunMediaProps> = ({ onBuildReport }) => {
  const [, getProjectRun] = useGetHeadlineDataProjectRun()
  const [, getHeadlineData] = useGetHeadlineData()
  // Handoff from Headline Data right-click "Go To Media".
  const [handoff, setHandoff] = useRecoilState(runMediaRunState)

  const [projectRunPairs, setProjectRunPairs] = useState<{ project: string; run: string }[]>([])
  const [selectedProject, setSelectedProject] = useState<string | null>(null)
  const [selectedRun, setSelectedRun] = useState<string | null>(null)
  const [loadingRun, setLoadingRun] = useState(false)
  // The project/run whose media is currently loaded (may differ from the dropdown
  // values). Every working-set read/write is keyed off this identity, never the
  // dropdown, so changing the selection can't mis-file media against another run.
  const [loadedRun, setLoadedRun] = useState<string | null>(null)
  const [loadedProject, setLoadedProject] = useState<string | null>(null)

  const [cases, setCases] = useState<RunCase[]>([])
  const [runCases, setRunCases] = useState<ReportRunCase[]>([])
  const [runMedia, setRunMedia] = useState<RunMediaRecord | null>(null)
  // Local working set for "Selected Media for the Report". Persisted on Save.
  const [draftItems, setDraftItems] = useState<RunMediaItem[]>([])
  // All marked-up images for the run (a browse library, like GIFs).
  const [markupLibrary, setMarkupLibrary] = useState<RunMediaItem[]>([])
  const [saving, setSaving] = useState(false)
  const [caseFilter, setCaseFilter] = useState<string | null>(null)

  // Available-media browse state (shown inline on the page).
  const [pickerCaseId, setPickerCaseId] = useState<string | null>(null)
  const [pickerImages, setPickerImages] = useState<PostProcImage[]>([])
  const [pickerGifs, setPickerGifs] = useState<GifJob[]>([])
  const [pickerGeometry, setPickerGeometry] = useState<
    { url: string; name: string; blobPath?: string; gmid?: string }[]
  >([])
  const [pickerMav, setPickerMav] = useState<MavMediaForCase[]>([])
  const [pickerLoading, setPickerLoading] = useState(false)
  // Post-proc image filters mirroring the Headline Data gallery.
  const [pickerImageType, setPickerImageType] = useState<string | null>(null)
  const [pickerSectionFilter, setPickerSectionFilter] = useState<string | null>(null)
  const [pickerLocation, setPickerLocation] = useState<string | null>(null)
  const [pickerMetricTypes, setPickerMetricTypes] = useState<string[]>([])
  const [pickerFilters, setPickerFilters] = useState<PostProcImageFilters | null>(null)
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [showGifModal, setShowGifModal] = useState(false)
  const [gifSaving, setGifSaving] = useState(false)
  const [showMarkupModal, setShowMarkupModal] = useState(false)
  const [markupSaving, setMarkupSaving] = useState(false)
  const uploadInputRef = useRef<HTMLInputElement>(null)

  // --- Project / run selection -------------------------------------------
  useEffect(() => {
    let cancelled = false
    getProjectRun({ userId: '', page: 0, pageSize: 1000 } as any)
      .then((res: any) => {
        if (cancelled) return
        // The project/run endpoint returns raw rows: [project, run, ...].
        const rows = (res?.data?.result?.data_array || []) as any[][]
        const pairs = rows
          .map(r => ({ project: String(r[0] ?? ''), run: String(r[1] ?? '') }))
          .filter(p => p.project && p.run)
        const seen = new Set<string>()
        setProjectRunPairs(
          pairs.filter(p => {
            const k = `${p.project}::${p.run}`
            if (seen.has(k)) return false
            seen.add(k)
            return true
          })
        )
      })
      .catch(() => {
        /* selector stays empty; the handoff still works */
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const projectOptions = useMemo(() => {
    const set = new Set(projectRunPairs.map(p => p.project))
    return Array.from(set)
      .sort()
      .map(p => ({ label: p, value: p }))
  }, [projectRunPairs])

  const runOptions = useMemo(() => {
    if (!selectedProject) return []
    return projectRunPairs
      .filter(p => p.project === selectedProject)
      .map(p => ({ label: p.run, value: p.run }))
  }, [projectRunPairs, selectedProject])

  const loadRun = async () => {
    if (!selectedProject || !selectedRun) return
    // Warn before discarding a different run's unsaved working set.
    if (dirty && loadedRun && (loadedRun !== selectedRun || loadedProject !== selectedProject)) {
      const proceed = window.confirm(
        `You have unsaved media changes for "${loadedRun}". Loading "${selectedRun}" will switch runs ` +
          `without saving. Your changes are cached locally, but click Save to keep them. Continue?`
      )
      if (!proceed) return
    }
    setLoadingRun(true)
    try {
      const res: any = await getHeadlineData({
        page: 0,
        pageSize: 10000,
        projects: selectedProject
      } as any)
      const allRows = parseHeadlineRows(res?.data)
      const byGm = new Map<string, Record<string, any>>()
      allRows.forEach(r => {
        if (r.gm_id != null) byGm.set(String(r.gm_id), r)
      })
      const deltaPairs: [keyof ReportRunCase, string][] = [
        ['delta_cd', 'cd'],
        ['delta_cl', 'cl'],
        ['delta_clf', 'clf'],
        ['delta_clr', 'clr'],
        ['delta_csf', 'csf'],
        ['delta_csr', 'csr'],
        ['delta_pct_clf', 'pct_clf']
      ]
      const rows = allRows.filter(r => String(r.run) === selectedRun)
      const byState = new Map<string, ReportRunCase>()
      rows.forEach(r => {
        const gmId = String(r.gm_id ?? '')
        if (!gmId) return
        const ref = r.ref_gm_id != null ? byGm.get(String(r.ref_gm_id)) : undefined
        const entry: ReportRunCase = {
          gmId,
          systemRunId: `0-${gmId}`,
          stateName: r.state_name ? String(r.state_name) : undefined,
          refGmId: r.ref_gm_id != null ? String(r.ref_gm_id) : undefined,
          yaw: toNum(r.yaw),
          steer: toNum(r.steer),
          roll: toNum(r.roll),
          speed: toNum(r.speed)
        }
        deltaPairs.forEach(([deltaKey, coeffKey]) => {
          const a = toNum(r[coeffKey])
          const b = ref ? toNum(ref[coeffKey]) : undefined
          ;(entry as any)[deltaKey] = a != null && b != null ? a - b : toNum(r[deltaKey as string])
        })
        byState.set(String(r.state_name ?? gmId), entry)
      })
      const list = Array.from(byState.values())
      if (!list.length) {
        toast.error('No cases found for that run')
        return
      }
      setRunCases(list)
      const caseList = list.map(c => ({
        gmId: c.gmId,
        systemRunId: c.systemRunId,
        stateName: c.stateName
      }))
      setCases(caseList)
      setPickerCaseId(caseList[0]?.systemRunId ?? null)
      setCaseFilter(null)
      await loadRunMedia(selectedRun, selectedProject ?? undefined)
      loadAvailableMedia(selectedRun, caseList)
      setLoadedRun(selectedRun)
      setLoadedProject(selectedProject)
    } catch {
      toast.error('Failed to load run')
    } finally {
      setLoadingRun(false)
    }
  }

  const loadRunMedia = async (runName: string, project?: string) => {
    try {
      const rec = await getRunMedia(runName, project)
      setRunMedia(rec)
      const serverMarkup = (rec?.items ?? []).filter(i => i.source === 'markup')
      const serverReport = (rec?.items ?? []).filter(i => i.source !== 'markup')
      // Restore any unsaved local edits, otherwise start from the saved set.
      const cached = readDraftCache(runName, project)
      setDraftItems(cached?.items ?? serverReport)
      setMarkupLibrary(cached?.markupLibrary ?? serverMarkup)
    } catch {
      toast.error('Failed to load run media')
    }
  }

  // Apply a run handed off from Headline Data's "Go To Media".
  useEffect(() => {
    if (!handoff) return
    if (!handoff.cases?.length) {
      toast.error('No cases found for the selected run')
    } else {
      setSelectedProject(handoff.project || null)
      setSelectedRun(handoff.runName || null)
      setRunCases(handoff.cases)
      const caseList = handoff.cases.map(c => ({
        gmId: c.gmId,
        systemRunId: c.systemRunId,
        stateName: c.stateName
      }))
      setCases(caseList)
      setPickerCaseId(caseList[0]?.systemRunId ?? null)
      setCaseFilter(null)
      void loadRunMedia(handoff.runName, handoff.project || undefined)
      loadAvailableMedia(handoff.runName, caseList)
      setLoadedRun(handoff.runName || null)
      setLoadedProject(handoff.project || null)
      toast.success(`Loaded run ${handoff.runName} (${handoff.cases.length} cases)`)
    }
    setHandoff(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoff?.token])

  // Restore the previous working session when returning to the tab, so the
  // loaded run and its working media set come back without reloading.
  const restoredRef = useRef(false)
  useEffect(() => {
    if (restoredRef.current) return
    restoredRef.current = true
    if (handoff) return // an incoming handoff takes precedence
    const snap = readSessionCache()
    if (!snap?.runName) return
    setSelectedProject(snap.project ?? null)
    setSelectedRun(snap.runName)
    setRunCases(snap.runCases ?? [])
    setCases(snap.cases ?? [])
    setDraftItems(snap.draftItems ?? [])
    setMarkupLibrary(snap.markupLibrary ?? [])
    setPickerCaseId(snap.cases?.[0]?.systemRunId ?? null)
    setCaseFilter(null)
    setLoadedRun(snap.runName)
    setLoadedProject(snap.project ?? null)
    // Refresh the saved baseline (for the dirty indicator) without clobbering the draft.
    getRunMedia(snap.runName, snap.project)
      .then(setRunMedia)
      .catch(() => {})
    loadAvailableMedia(snap.runName, snap.cases ?? [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // --- Main grid ---------------------------------------------------------
  const items = draftItems
  const caseFilterOptions = useMemo(() => {
    const opts = cases.map(c => ({
      label: c.stateName ? `${c.gmId} · ${c.stateName}` : c.gmId,
      value: c.systemRunId
    }))
    return [{ label: 'All cases', value: null as string | null }, ...opts]
  }, [cases])

  const visibleItems = useMemo(
    () => (caseFilter ? items.filter(i => i.caseKey === caseFilter) : items),
    [items, caseFilter]
  )

  // Marked-up images the user has added to the report (shown with a ✓ in the Mark-Up library).
  const reportItemIds = useMemo(() => new Set(draftItems.map(i => i.id)), [draftItems])

  const markupItems = useMemo(() => {
    return caseFilter ? markupLibrary.filter(i => i.caseKey === caseFilter) : markupLibrary
  }, [markupLibrary, caseFilter])

  // Urls already in the working set — used to mark browse tiles as added.
  const addedUrls = useMemo(
    () => new Set(draftItems.map(i => i.url).filter(Boolean) as string[]),
    [draftItems]
  )

  // Union of the report selection and the markup library — the full saved collection.
  const collectionItems = useMemo(() => {
    const map = new Map<string, RunMediaItem>()
    ;[...markupLibrary, ...draftItems].forEach(i => map.set(i.id, i))
    return Array.from(map.values())
  }, [markupLibrary, draftItems])

  // Unsaved changes = pending additions or removals of previously-saved items.
  const { pendingCount, removedCount, dirty } = useMemo(() => {
    const ids = new Set(collectionItems.map(i => i.id))
    const pending = collectionItems.filter(i => isPending(i.id)).length
    const removed = (runMedia?.items ?? []).filter(i => !ids.has(i.id)).length
    return { pendingCount: pending, removedCount: removed, dirty: pending > 0 || removed > 0 }
  }, [collectionItems, runMedia])

  // Persist the working set locally so edits survive navigating away.
  useEffect(() => {
    if (!loadedRun) return
    if (dirty) writeDraftCache(loadedRun, loadedProject ?? undefined, draftItems, markupLibrary)
    else clearDraftCache(loadedRun, loadedProject ?? undefined)
  }, [draftItems, markupLibrary, dirty, loadedRun, loadedProject])

  // Snapshot the whole working session (run, cases, media) for tab returns.
  useEffect(() => {
    if (!loadedRun || !cases.length) return
    writeSessionCache({
      project: loadedProject ?? undefined,
      runName: loadedRun,
      cases,
      runCases,
      draftItems,
      markupLibrary
    })
  }, [loadedRun, loadedProject, cases, runCases, draftItems, markupLibrary])

  const removeItem = (item: RunMediaItem) => {
    setDraftItems(prev => prev.filter(i => i.id !== item.id))
  }

  // Persist the working set: add new (pending) items and delete removed ones.
  const saveChanges = async () => {
    if (!loadedRun || !dirty || saving) return
    setSaving(true)
    try {
      const ids = new Set(collectionItems.map(i => i.id))
      const pending = collectionItems.filter(i => isPending(i.id) && i.url)
      const removed = (runMedia?.items ?? []).filter(i => !ids.has(i.id))
      // Remember which markups were in the report so we can restore that after reload.
      const reportMarkupCaptions = new Set(
        draftItems.filter(i => i.source === 'markup').map(i => i.caption)
      )
      if (pending.length) {
        await addRunMedia(
          loadedRun,
          pending.map(i => ({
            url: i.url as string,
            caption: i.caption,
            type: i.type,
            source: i.source,
            caseKey: i.caseKey,
            caseName: i.caseName
          })),
          loadedProject ?? undefined
        )
      }
      for (const r of removed) {
        await removeRunMediaItem(loadedRun, r.id, loadedProject ?? undefined)
      }
      const rec = await getRunMedia(loadedRun, loadedProject ?? undefined)
      setRunMedia(rec)
      const serverMarkup = (rec?.items ?? []).filter(i => i.source === 'markup')
      const serverReport = (rec?.items ?? []).filter(i => i.source !== 'markup')
      setMarkupLibrary(serverMarkup)
      setDraftItems([
        ...serverReport,
        ...serverMarkup.filter(i => reportMarkupCaptions.has(i.caption))
      ])
      clearDraftCache(loadedRun, loadedProject ?? undefined)
      toast.success('Saved')
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const buildReport = () => {
    if (!loadedProject || !loadedRun) return
    const withUrl = draftItems.filter(i => i.url)
    if (!withUrl.length) {
      toast(
        'No media selected — the report will open with only the run\u2019s cases and geometry.',
        {
          icon: '\u2139\uFE0F'
        }
      )
    }
    const toRef = (i: RunMediaItem) => ({
      url: i.url as string,
      caption: i.caption,
      type: i.type
    })
    onBuildReport?.({
      project: loadedProject,
      runName: loadedRun,
      cases: runCases,
      // Marked-up images get their own report section; everything else is run media.
      media: withUrl.filter(i => i.source !== 'markup').map(toRef),
      markupMedia: withUrl.filter(i => i.source === 'markup').map(toRef),
      token: Date.now()
    })
  }

  // --- Available media (browsed inline) ----------------------------------
  const runSystemRunIds = useMemo(() => new Set(cases.map(c => c.systemRunId)), [cases])

  // Refresh cached working-set URLs for one source from a fresh caption→url map
  // (source SAS links expire while unsaved items sit in the local cache).
  const applyUrlRefresh = (source: RunMediaItem['source'], byCaption: Map<string, string>) => {
    if (!byCaption.size) return
    const remap = (list: RunMediaItem[]) =>
      list.map(i =>
        i.source === source && i.caption && byCaption.has(i.caption)
          ? { ...i, url: byCaption.get(i.caption) }
          : i
      )
    setDraftItems(prev => remap(prev))
    setMarkupLibrary(prev => remap(prev))
  }

  // Load the run-level available media (GIFs, geometry, MAV) when a run loads.
  const loadAvailableMedia = (runName: string, caseList: RunCase[]) => {
    listGifs(50)
      .then(jobs => {
        const completed = jobs.filter(g => g.status === 'completed' && g.resultUrl)
        setPickerGifs(completed)
        applyUrlRefresh('gif', new Map(completed.map(g => [g.name, g.resultUrl as string])))
      })
      .catch(() => setPickerGifs([]))
    getRunGeometryImages(runName)
      .then(geo => {
        setPickerGeometry(geo)
        applyUrlRefresh('geometry', new Map(geo.map(g => [g.name, g.url])))
      })
      .catch(() => setPickerGeometry([]))
    // MAV media is keyed by the case key MAV sent (systemRunId or gm_id).
    const caseKeys = [...caseList.map(c => c.systemRunId), ...caseList.map(c => c.gmId)]
    if (caseKeys.length) {
      getMavMediaForCases(caseKeys)
        .then(mav => {
          setPickerMav(mav)
          applyUrlRefresh('mav', new Map(mav.map(m => [m.caption || m.name, m.url])))
        })
        .catch(() => setPickerMav([]))
    } else {
      setPickerMav([])
    }
  }

  // Seed available filter options when the selected case changes, and reset filters.
  useEffect(() => {
    setPickerImageType(null)
    setPickerSectionFilter(null)
    setPickerLocation(null)
    setPickerMetricTypes([])
    setPickerFilters(null)
    if (!pickerCaseId) return
    let cancelled = false
    fetchCaseImages(pickerCaseId, {}, 1)
      .then(res => !cancelled && setPickerFilters(res.availableFilters ?? null))
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [pickerCaseId])

  // Post-proc images are per-case; (re)load when the case or any filter changes.
  useEffect(() => {
    if (!pickerCaseId) {
      setPickerImages([])
      return
    }
    let cancelled = false
    setPickerLoading(true)
    fetchCaseImages(pickerCaseId, {
      imageType: pickerImageType,
      section: pickerSectionFilter,
      location: pickerLocation,
      metricTypes: pickerMetricTypes
    })
      .then(res => {
        if (cancelled) return
        const imgs = res.images ?? []
        setPickerImages(imgs)
        applyUrlRefresh('postproc', new Map(imgs.map(im => [im.name, im.url])))
      })
      .catch(() => !cancelled && toast.error('Failed to load images'))
      .finally(() => !cancelled && setPickerLoading(false))
    return () => {
      cancelled = true
    }
  }, [pickerCaseId, pickerImageType, pickerSectionFilter, pickerLocation, pickerMetricTypes])

  // Filter dropdown options, seeded from the case's availableFilters.
  const pickerImageTypeOptions = useMemo(() => {
    const types = pickerFilters?.imageTypes ?? []
    return [
      { label: 'All Types', value: null },
      ...types.map(t => ({ label: t === 'Sect' ? 'Section' : t, value: t }))
    ]
  }, [pickerFilters?.imageTypes])
  const pickerSectionOptions = useMemo(() => {
    const list = pickerFilters?.sections ?? []
    return [{ label: 'All Sect.', value: null }, ...list.map(s => ({ label: s, value: s }))]
  }, [pickerFilters?.sections])
  const pickerLocationOptions = useMemo(() => {
    const list = pickerFilters?.locations ?? []
    return [{ label: 'All Loc.', value: null }, ...list.map(l => ({ label: l, value: l }))]
  }, [pickerFilters?.locations])
  const pickerMetricTypeOptions = useMemo(() => {
    const types = pickerFilters?.metricTypes ?? []
    const referenceMetrics = pickerFilters?.referenceMetrics ?? []
    const compareOptions = referenceMetrics.map(rm => ({
      label: `${rm.metricType} (${rm.referenceRunId})`,
      value: `${rm.metricType}||${rm.referenceRunId}`
    }))
    return [...types.map(t => ({ label: t, value: t })), ...compareOptions]
  }, [pickerFilters?.metricTypes, pickerFilters?.referenceMetrics])

  const currentCaseName = useMemo(() => {
    const c = cases.find(x => x.systemRunId === pickerCaseId)
    return c?.stateName || c?.gmId
  }, [cases, pickerCaseId])

  const visibleGifs = useMemo(
    () =>
      pickerGifs.filter(g =>
        (g.frames ?? []).some(fr => fr.caseId && runSystemRunIds.has(fr.caseId))
      ),
    [pickerGifs, runSystemRunIds]
  )

  // Still images the user can open in the Markup modal.
  const markupImages = useMemo<MarkupImage[]>(() => {
    const out: MarkupImage[] = []
    const seen = new Set<string>()
    const add = (url?: string, name?: string) => {
      if (!url || seen.has(url)) return
      seen.add(url)
      out.push({ url, name: name || 'image' })
    }
    pickerImages.forEach(img => add(img.url, img.name))
    pickerGeometry.forEach(g => add(g.url, g.name))
    pickerMav.filter(m => m.type === 'image').forEach(m => add(m.url, m.caption || m.name))
    draftItems.filter(i => i.type === 'image').forEach(i => add(i.url, i.caption || i.caseName))
    return out
  }, [pickerImages, pickerGeometry, pickerMav, draftItems])

  // Upload a marked-up image and add it to the run's Mark-Up library.
  const handleMarkupSave = async (file: File, caption: string) => {
    setMarkupSaving(true)
    try {
      const refs = await uploadSectionMedia([file])
      const drafts = refs.map(r =>
        candidateToDraftItem({
          url: r.url,
          caption: caption || r.caption || '',
          type: 'image',
          source: 'markup',
          caseKey: pickerCaseId || undefined,
          caseName: currentCaseName
        })
      )
      setMarkupLibrary(prev => [...prev, ...drafts])
      toast.success('Markup saved to the Mark-Up library')
      setShowMarkupModal(false)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to save markup')
    } finally {
      setMarkupSaving(false)
    }
  }

  // Add a marked-up image from the library into the report selection.
  const addMarkupToReport = (item: RunMediaItem) => {
    if (reportItemIds.has(item.id)) return
    setDraftItems(prev => [...prev, item])
  }

  // Add a finished GIF straight into the working set.
  const handleGifSaveToRunMedia = async (gif: GifJob) => {
    if (!gif.resultUrl) return
    setGifSaving(true)
    try {
      setDraftItems(prev => [
        ...prev,
        candidateToDraftItem({
          url: gif.resultUrl as string,
          caption: gif.name || 'Animated GIF',
          type: 'image',
          source: 'gif',
          caseName: currentCaseName
        })
      ])
      toast.success('GIF added to run media')
      setShowGifModal(false)
    } finally {
      setGifSaving(false)
    }
  }

  // Add one browsed item into the local working set (persisted on Save).
  const addOne = (cand: Candidate) => {
    if (!loadedRun || addedUrls.has(cand.url)) return
    setDraftItems(prev => [...prev, candidateToDraftItem(cand)])
  }

  const handleUploadFiles = async (fileList: FileList | null) => {
    if (!loadedRun || !fileList?.length) return
    await uploadFiles(Array.from(fileList))
    if (uploadInputRef.current) uploadInputRef.current.value = ''
  }

  // Shared upload path for the file picker, drag-and-drop and clipboard paste.
  const uploadFiles = async (files: File[]) => {
    const media = files.filter(f => f.type.startsWith('image/') || f.type.startsWith('video/'))
    if (!loadedRun || !media.length) return
    setUploading(true)
    try {
      const refs = await uploadSectionMedia(media)
      const drafts = refs.map(r =>
        candidateToDraftItem({
          url: r.url,
          caption: r.caption ?? '',
          type: r.type ?? 'image',
          source: 'upload',
          caseKey: pickerCaseId || undefined,
          caseName: currentCaseName
        })
      )
      setDraftItems(prev => [...prev, ...drafts])
      toast.success(`Added ${drafts.length} file${drafts.length === 1 ? '' : 's'}`)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  // Keep the latest uploadFiles for the document-level paste listener below.
  const uploadFilesRef = useRef(uploadFiles)
  uploadFilesRef.current = uploadFiles

  // Let users paste images/videos from the clipboard while a run is loaded.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (!loadedRun) return
      const target = e.target as HTMLElement | null
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return
      }
      const media = Array.from(e.clipboardData?.files ?? []).filter(
        f => f.type.startsWith('image/') || f.type.startsWith('video/')
      )
      if (!media.length) return
      e.preventDefault()
      void uploadFilesRef.current(media)
    }
    document.addEventListener('paste', onPaste)
    return () => document.removeEventListener('paste', onPaste)
  }, [loadedRun])

  // Accept files dropped onto the selected-media zone.
  const handleDrop = (e: DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (!loadedRun) return
    void uploadFiles(Array.from(e.dataTransfer.files))
  }

  const sourceBadge = (source: RunMediaItem['source']) => {
    const label =
      source === 'postproc'
        ? 'Post-Proc'
        : source === 'gif'
        ? 'GIF'
        : source === 'geometry'
        ? 'Geometry'
        : source === 'mav'
        ? 'MAV'
        : source === 'markup'
        ? 'Markup'
        : 'Upload'
    return (
      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gm-blue/10 text-gm-blue">
        {label}
      </span>
    )
  }

  const Thumb: FC<{ url?: string; type: 'image' | 'video'; alt?: string }> = ({
    url,
    type,
    alt
  }) =>
    type === 'video' ? (
      <video src={url} muted preload="metadata" className="w-full h-28 object-cover bg-black" />
    ) : (
      <img src={url} alt={alt} className="w-full h-28 object-cover" />
    )

  // Geometry-change images live in the cfd-data container, which is not reachable
  // directly from the browser (unlike post-proc images in cfd-cases). Fetch the
  // bytes through the DownloadBlobProxy and render them from an object URL.
  const GeometryThumb: FC<{ blobPath?: string; gmid?: string; alt?: string }> = ({
    blobPath,
    gmid,
    alt
  }) => {
    const [src, setSrc] = useState<string>('')
    const [failed, setFailed] = useState(false)
    useEffect(() => {
      if (!blobPath) {
        setFailed(true)
        return
      }
      let objectUrl = ''
      let cancelled = false
      downloadFileDirectFromBlob(
        async () => '',
        UploadTypes.GEOMETRY_CHANGE_FILE,
        blobPath,
        null,
        gmid
      )
        .then(blob => {
          if (cancelled) return
          objectUrl = URL.createObjectURL(blob)
          setSrc(objectUrl)
        })
        .catch(() => {
          if (!cancelled) setFailed(true)
        })
      return () => {
        cancelled = true
        if (objectUrl) URL.revokeObjectURL(objectUrl)
      }
    }, [blobPath, gmid])
    if (failed) {
      return (
        <div className="w-full h-24 flex items-center justify-center bg-gray-100 dark:bg-neutral-70 text-[10px] text-gray-400">
          Unavailable
        </div>
      )
    }
    if (!src) {
      return (
        <div className="w-full h-24 flex items-center justify-center bg-gray-100 dark:bg-neutral-70">
          <ProgressSpinner style={{ width: '1.5rem', height: '1.5rem' }} strokeWidth="6" />
        </div>
      )
    }
    return <img src={src} alt={alt} className="w-full h-24 object-cover" />
  }

  // A clickable browse tile that adds the item to the report media on click.
  const browseTile = (cand: Candidate, keyId: string, thumb: JSX.Element) => {
    const added = addedUrls.has(cand.url)
    return (
      <button
        key={keyId}
        onClick={() => addOne(cand)}
        disabled={added}
        title={added ? 'Already in report media' : 'Add to report media'}
        className={classNames('relative rounded overflow-hidden border-2 group', {
          'border-green-500': added,
          'border-transparent hover:border-gm-blue': !added
        })}>
        {thumb}
        <span
          className={classNames(
            'absolute top-1 right-1 rounded-full w-5 h-5 text-xs flex items-center justify-center',
            added
              ? 'bg-green-500 text-white'
              : 'bg-black/60 text-white opacity-0 group-hover:opacity-100'
          )}>
          {added ? '✓' : '+'}
        </span>
      </button>
    )
  }

  return (
    <div className="run-media p-4 space-y-4">
      {/* Shorten dropdowns so their height matches the buttons. */}
      <style>{`
        .run-media [data-pc-name="dropdown"] [data-pc-section="input"] {
          padding-top: .5rem !important;
          padding-bottom: .5rem !important;
        }
      `}</style>
      {/* Selection */}
      <div className="dark:bg-neutral-80 bg-white p-4 rounded-md border border-gray-200 dark:border-neutral-70">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-semibold mb-1 dark:text-neutral-10">Run Media</h3>
            <p className="text-sm text-gray-600 dark:text-neutral-20">
              A single media library for a run — post-proc images, GIFs, geometry images, MAV
              uploads and local files are shown below. Click any item to add it to the report media,
              then build a report.
            </p>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <span className="flex flex-col gap-1">
              <label className="text-xs text-gray-600 dark:text-neutral-20">Project</label>
              <Dropdown
                value={selectedProject}
                options={projectOptions}
                onChange={e => {
                  setSelectedProject(e.value)
                  setSelectedRun(null)
                }}
                placeholder="Select project"
                filter
                className="w-56 text-sm"
                tooltip="Select a project to list its runs"
                tooltipOptions={{ position: 'top' }}
              />
            </span>
            <span className="flex flex-col gap-1">
              <label className="text-xs text-gray-600 dark:text-neutral-20">Run</label>
              <Dropdown
                value={selectedRun}
                options={runOptions}
                onChange={e => setSelectedRun(e.value)}
                placeholder="Select run"
                filter
                disabled={!selectedProject}
                className="w-56 text-sm"
                tooltip={!selectedProject ? 'Select a project first' : 'Select a run to load'}
                tooltipOptions={{ position: 'top' }}
              />
            </span>
            <Button
              label="Load Run"
              icon="pi pi-download"
              onClick={loadRun}
              loading={loadingRun}
              disabled={!selectedRun}
              tooltip={!selectedRun ? 'Select a run first' : "Load this run's media library"}
              tooltipOptions={{ position: 'top' }}
            />
          </div>
        </div>
      </div>

      {runMedia !== null || cases.length > 0 ? (
        <>
          {/* Selected media for the report (top) */}
          <div
            onDragOver={e => {
              e.preventDefault()
              if (!dragOver) setDragOver(true)
            }}
            onDragLeave={e => {
              e.preventDefault()
              setDragOver(false)
            }}
            onDrop={handleDrop}
            aria-label="Selected media — paste, upload, or drag and drop images or videos here"
            className={classNames(
              'dark:bg-neutral-80 bg-white p-4 rounded-md border transition-colors',
              dragOver
                ? 'border-gm-blue border-dashed ring-2 ring-gm-blue/40'
                : 'border-gray-200 dark:border-neutral-70'
            )}>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h3 className="font-semibold dark:text-neutral-10">
                Selected Media for the Report{' '}
                <span className="text-xs font-normal text-gray-500 dark:text-neutral-60">
                  · {items.length} item{items.length === 1 ? '' : 's'}
                </span>
                {dirty && (
                  <span
                    className="ml-2 text-xs font-medium text-amber-600 dark:text-amber-400"
                    title="Changes not yet saved to the run's media library">
                    · Unsaved{' '}
                    {[
                      pendingCount ? `${pendingCount} added` : '',
                      removedCount ? `${removedCount} removed` : ''
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                <Dropdown
                  value={caseFilter}
                  options={caseFilterOptions}
                  onChange={e => setCaseFilter(e.value)}
                  placeholder="All cases"
                  className="w-52 text-sm"
                  tooltip="Filter selected media by case"
                  tooltipOptions={{ position: 'top' }}
                />
                <Button
                  label="Upload"
                  icon="pi pi-upload"
                  outlined
                  loading={uploading}
                  disabled={!loadedRun}
                  onClick={() => uploadInputRef.current?.click()}
                  tooltip={
                    !loadedRun
                      ? 'Load a run first'
                      : 'Upload, paste (Ctrl+V) or drag-and-drop images or videos'
                  }
                  tooltipOptions={{ position: 'top' }}
                />
                <Button
                  label="Save"
                  icon="pi pi-save"
                  onClick={saveChanges}
                  loading={saving}
                  disabled={!dirty}
                  tooltip={!dirty ? 'No changes to save' : "Save this run's media library"}
                  tooltipOptions={{ position: 'top' }}
                />
                <Button
                  label="Build Report"
                  icon="pi pi-file"
                  outlined
                  onClick={buildReport}
                  disabled={!loadedRun}
                  tooltip={
                    !loadedRun
                      ? 'Load a run first'
                      : `Open the Report Generator with the loaded run "${loadedRun}"`
                  }
                  tooltipOptions={{ position: 'top' }}
                />
              </div>
            </div>

            {visibleItems.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-neutral-60">
                No media selected yet. Click any item under <strong>Available Media</strong> below
                to add it here, or <strong>upload</strong>, <strong>paste</strong> (Ctrl+V) or{' '}
                <strong>drag &amp; drop</strong> image/video files here.
              </p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {visibleItems.map(item => (
                  <figure
                    key={item.id}
                    className="m-0 rounded overflow-hidden border border-gray-200 dark:border-neutral-70 relative group">
                    <Thumb url={item.url} type={item.type} alt={item.caption} />
                    <button
                      className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-6 h-6 text-xs opacity-0 group-hover:opacity-100"
                      title="Remove from selected media"
                      onClick={() => removeItem(item)}>
                      ✕
                    </button>
                    <figcaption className="px-2 py-1 space-y-1">
                      <div className="flex items-center gap-1">{sourceBadge(item.source)}</div>
                      <div className="text-xs text-gray-600 dark:text-neutral-20 truncate">
                        {item.caption || item.caseName || ''}
                      </div>
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}
          </div>

          {/* Mark-Up — a browse library of marked-up images; click to add to the report */}
          <div className="dark:bg-neutral-80 bg-white p-4 rounded-md border border-gray-200 dark:border-neutral-70">
            <h3 className="font-semibold dark:text-neutral-10 mb-3">
              Mark-Up{' '}
              <span className="text-xs font-normal text-gray-500 dark:text-neutral-60">
                · {markupItems.length} item{markupItems.length === 1 ? '' : 's'} · click to add to
                the report
              </span>
            </h3>
            {markupItems.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-neutral-60">
                No marked-up images yet. Use <strong>Mark-Up</strong> under Available Media to
                annotate an image — it will appear here to add to the report.
              </p>
            ) : (
              <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                {markupItems.map(item => {
                  const added = reportItemIds.has(item.id)
                  return (
                    <button
                      key={item.id}
                      onClick={() => addMarkupToReport(item)}
                      disabled={added}
                      title={added ? 'Already in report media' : 'Add to report media'}
                      className={classNames('relative rounded overflow-hidden border-2 group', {
                        'border-green-500': added,
                        'border-transparent hover:border-gm-blue': !added
                      })}>
                      <img src={item.url} alt={item.caption} className="w-full h-24 object-cover" />
                      <span
                        className={classNames(
                          'absolute top-1 right-1 rounded-full w-5 h-5 text-xs flex items-center justify-center',
                          added
                            ? 'bg-green-500 text-white'
                            : 'bg-black/60 text-white opacity-0 group-hover:opacity-100'
                        )}>
                        {added ? '✓' : '+'}
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Available media (browsed inline) */}
          {cases.length > 0 && (
            <div className="dark:bg-neutral-80 bg-white p-4 rounded-md border border-gray-200 dark:border-neutral-70">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <h3 className="font-semibold dark:text-neutral-10 mr-2">Available Media</h3>
                <label className="text-sm text-gray-600 dark:text-neutral-20">Case</label>
                <Dropdown
                  value={pickerCaseId}
                  options={cases.map(c => ({
                    label: c.stateName ? `${c.gmId} · ${c.stateName}` : c.gmId,
                    value: c.systemRunId
                  }))}
                  onChange={e => setPickerCaseId(e.value)}
                  placeholder="Select a case"
                  className="w-56"
                  tooltip="Choose a case to browse its media"
                  tooltipOptions={{ position: 'top' }}
                />
                {pickerLoading && (
                  <ProgressSpinner style={{ width: 20, height: 20 }} strokeWidth="4" />
                )}
                <span className="flex-1" />
                <input
                  ref={uploadInputRef}
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  className="hidden"
                  onChange={e => handleUploadFiles(e.target.files)}
                />
                <Button
                  label="Create GIF"
                  icon="pi pi-images"
                  outlined
                  disabled={!cases.length}
                  onClick={() => setShowGifModal(true)}
                  tooltip={
                    !cases.length
                      ? 'Load a run with cases first'
                      : 'Build an animated GIF from case images'
                  }
                  tooltipOptions={{ position: 'top' }}
                />
                <Button
                  label="Mark-Up"
                  icon="pi pi-pencil"
                  outlined
                  disabled={!markupImages.length}
                  onClick={() => setShowMarkupModal(true)}
                  tooltip={
                    !markupImages.length
                      ? 'No images available to mark up yet'
                      : 'Annotate an image and add it to the Mark-Up library'
                  }
                  tooltipOptions={{ position: 'top' }}
                />
                <Button
                  label="Upload"
                  icon="pi pi-upload"
                  outlined
                  loading={uploading}
                  onClick={() => uploadInputRef.current?.click()}
                  tooltip="Upload or paste (Ctrl+V) images or videos"
                  tooltipOptions={{ position: 'top' }}
                />
              </div>

              {/* MAV media (pushed via the upload API) */}
              {pickerMav.length > 0 && (
                <>
                  <h4 className="font-semibold mb-2 dark:text-neutral-10">MAV</h4>
                  <div className="grid grid-cols-4 md:grid-cols-6 gap-2 mb-4 max-h-60 overflow-y-auto">
                    {pickerMav.map(m =>
                      browseTile(
                        {
                          url: m.url,
                          caption: m.caption || m.name,
                          type: m.type,
                          source: 'mav',
                          caseKey: m.caseKey,
                          caseName: currentCaseName
                        },
                        m.url,
                        m.type === 'video' ? (
                          <video
                            src={m.url}
                            muted
                            preload="metadata"
                            className="w-full h-24 object-cover bg-black"
                          />
                        ) : (
                          <img src={m.url} alt={m.name} className="w-full h-24 object-cover" />
                        )
                      )
                    )}
                  </div>
                </>
              )}

              {/* Images */}
              <h4 className="font-semibold mb-2 dark:text-neutral-10">Images</h4>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Dropdown
                  value={pickerImageType}
                  options={pickerImageTypeOptions}
                  onChange={e => {
                    setPickerImageType(e.value)
                    setPickerSectionFilter(null)
                    setPickerLocation(null)
                    setPickerMetricTypes([])
                  }}
                  placeholder="All Types"
                  className="w-40 text-sm"
                  tooltip="Filter images by type"
                  tooltipOptions={{ position: 'top' }}
                />
                {pickerImageType === 'Sect' && (
                  <Dropdown
                    value={pickerSectionFilter}
                    options={pickerSectionOptions}
                    onChange={e => setPickerSectionFilter(e.value)}
                    placeholder="All Sect."
                    className="w-40 text-sm"
                    tooltip="Filter images by section"
                    tooltipOptions={{ position: 'top' }}
                  />
                )}
                {pickerImageType === 'Surface' && (
                  <Dropdown
                    value={pickerLocation}
                    options={pickerLocationOptions}
                    onChange={e => setPickerLocation(e.value)}
                    placeholder="All Loc."
                    className="w-40 text-sm"
                    tooltip="Filter images by surface location"
                    tooltipOptions={{ position: 'top' }}
                  />
                )}
                <MetricMultiSelect
                  value={pickerMetricTypes}
                  options={pickerMetricTypeOptions}
                  onChange={setPickerMetricTypes}
                  className="w-40 text-sm"
                />
              </div>
              {pickerImages.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-neutral-60 mb-4">
                  No images for this case.
                </p>
              ) : (
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2 mb-4 max-h-72 overflow-y-auto">
                  {pickerImages.map(img =>
                    browseTile(
                      {
                        url: img.url,
                        caption: img.name,
                        type: 'image',
                        source: 'postproc',
                        caseKey: pickerCaseId || undefined,
                        caseName: currentCaseName
                      },
                      img.fullPath,
                      <img src={img.url} alt={img.name} className="w-full h-24 object-cover" />
                    )
                  )}
                </div>
              )}

              {/* GIFs */}
              <h4 className="font-semibold mb-2 dark:text-neutral-10">GIFs</h4>
              {visibleGifs.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-neutral-60 mb-4">
                  No completed GIFs for this run.
                </p>
              ) : (
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2 mb-4 max-h-60 overflow-y-auto">
                  {visibleGifs.map(gif =>
                    gif.resultUrl
                      ? browseTile(
                          {
                            url: gif.resultUrl,
                            caption: gif.name,
                            type: 'image',
                            source: 'gif',
                            caseName: currentCaseName
                          },
                          gif.jobId,
                          <img
                            src={gif.resultUrl}
                            alt={gif.name}
                            className="w-full h-24 object-cover"
                          />
                        )
                      : null
                  )}
                </div>
              )}

              {/* Geometry */}
              <h4 className="font-semibold mb-2 dark:text-neutral-10">Geometry Changes</h4>
              {pickerGeometry.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-neutral-60">
                  No geometry images for this run.
                </p>
              ) : (
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2 max-h-60 overflow-y-auto">
                  {pickerGeometry.map(g =>
                    browseTile(
                      {
                        url: g.url,
                        caption: g.name,
                        type: 'image',
                        source: 'geometry',
                        caseName: currentCaseName
                      },
                      g.url,
                      <GeometryThumb blobPath={g.blobPath} gmid={g.gmid} alt={g.name} />
                    )
                  )}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="dark:bg-neutral-80 bg-white p-6 rounded-md border border-gray-200 dark:border-neutral-70 text-sm text-gray-500 dark:text-neutral-60">
          Select a project and run, then click <strong>Load Run</strong> to view its media and build
          a report.
        </div>
      )}

      <CreateGifModal
        isOpen={showGifModal}
        onClose={() => {
          setShowGifModal(false)
          // Newly created GIFs should appear in the browse grid.
          listGifs(50)
            .then(jobs => setPickerGifs(jobs.filter(g => g.status === 'completed' && g.resultUrl)))
            .catch(() => {})
        }}
        cases={cases.map<GifCaseOption>(c => ({
          systemRunId: c.systemRunId,
          caseName: c.stateName || c.gmId,
          runName: loadedRun ?? undefined,
          projectName: loadedProject ?? undefined
        }))}
        onSaveToRunMedia={handleGifSaveToRunMedia}
        savingToRunMedia={gifSaving}
      />

      <MarkupModal
        isOpen={showMarkupModal}
        onClose={() => setShowMarkupModal(false)}
        images={markupImages}
        onSave={handleMarkupSave}
        saving={markupSaving}
      />
    </div>
  )
}

export default RunMedia
